import React, { useState } from 'react';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import { STUDENT_CONSENT_TEXT } from 'scenes/Questionnaire';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import RouteUtils from 'services/RouteUtils';
import { Device } from 'services/Native/Device';
import type { Appointment, JitsiMeetingPayload } from 'generated/api';

interface JoinVideoButtonProps {
  appointment: Appointment;
  disabled?: boolean;
  id?: string;
}

const JoinVideoButton: React.FC<JoinVideoButtonProps> = ({ appointment, disabled }) => {
  const dispatch = useAppDispatch();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [submittingConsent, setSubmittingConsent] = useState(false);
  const [meetingData, setMeetingData] = useState<JitsiMeetingPayload>();
  const [loading, setLoading] = useState(false);

  const isMobileDevice = Device.isMobile();

  const handleJoinClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setLoading(true);
    const meetingData = await dispatch(
      api.Video.joinVideoAppointment2({ appointmentId: appointment.id! }),
      { errorTitle: 'Join Appointment' },
    );
    if (meetingData) {
      setMeetingData(meetingData);
      const shouldShowConsent =
        isMobileDevice && meetingData.questionnaire_code && meetingData.has_student;
      if (shouldShowConsent) {
        setShowConsentModal(true);
      } else {
        joinVideoCall(meetingData);
      }
    }
    setLoading(false);
  };

  const joinVideoCall = (meetingData: JitsiMeetingPayload) => {
    if (isMobileDevice) {
      Device.showTelemedicine(meetingData);
    } else {
      const route = RouteUtils.getRoute(RouteUtils.publicRoutes().telemedicine, {
        ':id': appointment.id,
      });
      const tab = window.open(route, '_blank');
      tab?.focus();
    }
  };

  const handleConsentSubmit = async (event: React.MouseEvent, consent: boolean) => {
    event.preventDefault();
    event.stopPropagation();
    setSubmittingConsent(true);
    const answer = consent ? 'Yes' : 'No';
    if (meetingData?.questionnaire_code) {
      await dispatch(
        api.Appointments.submitPortalQuestionnaire({
          id: appointment.id!,
          code: meetingData.questionnaire_code,
          questionnaire: {
            questions: [
              {
                question: STUDENT_CONSENT_TEXT,
                answer,
              },
            ],
          },
        }),
      );
    }

    setSubmittingConsent(false);
    setShowConsentModal(false);

    if (meetingData) {
      joinVideoCall(meetingData);
    }
  };

  return (
    <>
      <DefaultButton
        name="Join Video Appointment"
        className={'blue' + (!disabled ? ' active' : '')}
        icon="video"
        onClick={handleJoinClick}
        disabled={disabled}
        loading={loading}
        id="join-button"
      />

      {showConsentModal && (
        <ConfirmationDialog
          title="Student Consent"
          message={STUDENT_CONSENT_TEXT}
          open={showConsentModal}
          onConfirm={(event) => handleConsentSubmit(event, true)}
          onCancel={(event) => handleConsentSubmit(event, false)}
          loadingState={submittingConsent}
          confirmLabel="Yes, I consent"
          cancelLabel="No"
        />
      )}
    </>
  );
};

export default JoinVideoButton;
